import {
  Fragment,
  forwardRef,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import cn from "mcn";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { ValueSelectArrows } from "../icons/ValueSelectArrows";
import { CaretDownIcon } from "../icons/CaretDownIcon";
import ClickAwayListener from "../components/ClickAwayListener";
import { Tooltip, TooltipTrigger } from "@artis/ui";

export type DropdownOption = {
  value: string;
  label: string;
  disabled?: boolean;
  /** Explains why a disabled option cannot be picked; shown on hover anywhere in the row. */
  disabledTooltip?: string;
  /**
   * Optional group label. When provided, options sharing the same `groupValue`
   * are visually grouped under a sticky section header. Group order is
   * determined by the option order — the first option of each new group
   * starts a new section.
   */
  groupValue?: string;
};

export type DropdownProps = {
  options: DropdownOption[];
  value?: string;
  onChange?: (value: string) => void;
  error?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  icon?: "caret" | "arrows";
  /**
   * When `true` and a value is selected, the trailing caret is replaced with an
   * "X" icon. Clicking it invokes `onClear` instead of opening the menu,
   * allowing the value to be reset (e.g. back to a null / "n/a" state).
   */
  clearable?: boolean;
  onClear?: () => void;
  /**
   * Constrain the width of the dropdown (trigger + menu). Accepts any CSS width
   * value, e.g. `"60px"`. Applied as an inline `max-width` on the root element.
   */
  maxWidth?: string;
  /**
   * Set a fixed height for the dropdown trigger. Accepts any CSS height value,
   * e.g. `"31px"`. Applied as an inline `height` on the trigger button so the
   * control can be matched to sibling inputs.
   */
  height?: string;
  openAbove?: boolean;
  /**
   * Render the popup in a portal attached to document.body, positioned via
   * fixed coordinates relative to the viewport. This mirrors the behaviour of
   * the "Search Curves" dropdown — the menu can escape `overflow:hidden`
   * ancestors, and its max-height is computed from the viewport rather than
   * the nearest scrollable parent.
   */
  portal?: boolean;
};

/**
 * One option row. Disabled rows with a tooltip make the whole row the hover
 * target; the panel sits centred over the top of the row.
 */
function DropdownOptionRow({
  option,
  index,
  isHighlighted,
  isSelected,
  onSelect,
}: {
  option: DropdownOption;
  index: number;
  isHighlighted: boolean;
  isSelected: boolean;
  onSelect: (value: string) => void;
}) {
  const button = (
    <button
      type="button"
      role="option"
      data-option-value={option.value}
      data-dropdown-option-index={index}
      aria-disabled={option.disabled ? true : undefined}
      aria-selected={isHighlighted}
      onClick={() => !option.disabled && onSelect(option.value)}
      className={cn(
        "w-full text-left px-3 py-2 break-words flex items-center justify-between gap-2",
        !option.disabled && "text-[var(--foreground-default,var(--color-foreground))] hover:bg-[var(--neutral-tonal)]",
        isHighlighted && "bg-[var(--neutral-tonal)]",
        option.disabled &&
          "cursor-not-allowed text-[var(--neutral-on-disabled,var(--colour-text-on-disabled,#a3a3a3))]",
      )}
      style={{
        fontSize: "var(--font-size-base, 12px)",
      }}
    >
      <span
        className={
          option.disabled
            ? "text-[var(--neutral-on-disabled,var(--colour-text-on-disabled,#a3a3a3))]"
            : "text-[var(--foreground-default,var(--color-foreground))]"
        }
      >
        {option.label}
      </span>
      {isSelected && <span className="text-primary text-xs shrink-0">✓</span>}
    </button>
  );

  if (!option.disabled || !option.disabledTooltip) return button;

  return (
    <TooltipTrigger placement="top" origin="center" block>
      {button}
      <Tooltip>{option.disabledTooltip}</Tooltip>
    </TooltipTrigger>
  );
}

type PortalPosition = {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  flipUp: boolean;
  dropdownH: number;
};

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(function Dropdown(
  {
    options,
    value,
    onChange,
    error,
    fullWidth,
    placeholder,
    disabled,
    className,
    icon = "caret",
    clearable,
    onClear,
    maxWidth,
    height,
    openAbove,
    portal,
  },
  _ref,
) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const shouldScrollHighlightedRef = useRef(false);
  const shouldScrollSelectedOnOpenRef = useRef(false);
  const pendingKeyboardHighlightRef = useRef<"first" | "last" | null>(null);
  const prevIsOpenRef = useRef(isOpen);
  const prevValueRef = useRef(value);
  // -1 means no option is highlighted. Menus do not pre-highlight the first
  // (or selected) row on click-open; keyboard navigation can request a start row.
  const [highlightFlatIdx, setHighlightFlatIdx] = useState(-1);
  const [maxHeight, setMaxHeight] = useState("200px");
  const [openUpward, setOpenUpward] = useState(false);
  const [portalPosition, setPortalPosition] = useState<PortalPosition | null>(null);

  const selectableIndices = useMemo(
    () => options.map((o, i) => (o.disabled ? -1 : i)).filter((i): i is number => i >= 0),
    [options],
  );

  const selectedOption = options.find((opt) => opt.value === value);
  const displayValue = selectedOption?.label || placeholder || "";

  // Inline menu: measure before paint so we never flash “open below” then flip above.
  useLayoutEffect(() => {
    if (portal) return;
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();

      let container = dropdownRef.current.parentElement;
      let containerBottom = window.innerHeight;
      let containerTop = 0;
      while (container) {
        const style = getComputedStyle(container);
        if (
          style.overflow === "hidden" ||
          style.overflow === "auto" ||
          style.overflowY === "hidden" ||
          style.overflowY === "auto"
        ) {
          const containerRect = container.getBoundingClientRect();
          containerBottom = containerRect.bottom;
          containerTop = containerRect.top;
          break;
        }
        container = container.parentElement;
      }

      const spaceBelow = containerBottom - rect.bottom - 20;
      const spaceAbove = rect.top - containerTop - 20;
      const minRequiredSpace = 80;
      const maxDropdownHeight = 200;

      let shouldOpenUpward = false;
      if (openAbove !== undefined) {
        shouldOpenUpward = openAbove;
      } else {
        shouldOpenUpward = spaceBelow < minRequiredSpace && spaceAbove > spaceBelow;
      }

      setOpenUpward(shouldOpenUpward);
      const calculatedMaxHeight = shouldOpenUpward
        ? Math.min(maxDropdownHeight, Math.max(60, spaceAbove))
        : Math.min(maxDropdownHeight, Math.max(60, spaceBelow));
      setMaxHeight(`${calculatedMaxHeight}px`);
    }
  }, [isOpen, openAbove, portal]);

  // Portal variant: fixed coordinates on document.body (desktop / overflow escape).
  useEffect(() => {
    if (!portal) return;
    if (!isOpen || !dropdownRef.current) {
      setPortalPosition(null);
      return;
    }
    const anchorEl = dropdownRef.current;
    const updatePosition = () => {
      const rect = anchorEl.getBoundingClientRect();
      const reservedHeight = 500;
      const available = window.innerHeight - reservedHeight;
      const bounded = Math.min(available, 400);
      const maxH = available >= 200 ? Math.max(200, bounded) : Math.max(0, bounded);
      const spaceBelow = window.innerHeight - rect.bottom - 4;
      const spaceAbove = rect.top - 4;
      let flipUp: boolean;
      if (openAbove !== undefined) {
        flipUp = openAbove;
      } else {
        flipUp = spaceBelow < maxH && spaceAbove > spaceBelow;
      }
      const dropdownH = flipUp ? Math.min(maxH, spaceAbove) : Math.min(maxH, spaceBelow);
      const width = rect.width;
      const left = Math.min(rect.left, Math.max(0, window.innerWidth - width - 8));
      if (flipUp) {
        const bottom = Math.max(4, window.innerHeight - rect.top + 4);
        setPortalPosition({ bottom, left, width, flipUp, dropdownH });
      } else {
        const top = rect.bottom + 4;
        setPortalPosition({ top, left, width, flipUp, dropdownH });
      }
    };
    requestAnimationFrame(updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, portal, openAbove]);

  const handleSelect = (optionValue: string) => {
    onChange?.(optionValue);
    setIsOpen(false);
  };

  const safeHighlightFlat =
    highlightFlatIdx < 0 ? -1 : Math.min(highlightFlatIdx, Math.max(0, selectableIndices.length - 1));
  const highlightedOptionIndex =
    safeHighlightFlat < 0 ? undefined : (selectableIndices[safeHighlightFlat] ?? selectableIndices[0]);

  // Opening by click leaves every row unhighlighted. Arrow-open can request a start row.
  // If a value is already selected, still scroll it into view (checkmark, no tonal fill).
  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (!isOpen) {
      setHighlightFlatIdx(-1);
      return;
    }
    if (!justOpened || selectableIndices.length === 0) return;
    const pending = pendingKeyboardHighlightRef.current;
    pendingKeyboardHighlightRef.current = null;
    if (pending === "first") {
      setHighlightFlatIdx(0);
      shouldScrollHighlightedRef.current = true;
    } else if (pending === "last") {
      setHighlightFlatIdx(selectableIndices.length - 1);
      shouldScrollHighlightedRef.current = true;
    } else {
      setHighlightFlatIdx(-1);
      shouldScrollSelectedOnOpenRef.current = value !== undefined;
    }
  }, [isOpen, selectableIndices.length, value]);

  useEffect(() => {
    if (!isOpen || !shouldScrollSelectedOnOpenRef.current) return;
    const scrollToSelected = () => {
      const menuEl = menuRef.current;
      if (!menuEl || value === undefined) return false;
      const btn = menuEl.querySelector<HTMLElement>(`button[data-option-value="${CSS.escape(String(value))}"]`);
      if (!btn) return false;
      btn.scrollIntoView({ block: "nearest" });
      shouldScrollSelectedOnOpenRef.current = false;
      return true;
    };
    if (scrollToSelected()) return;
    // Portal menus mount after position is measured; retry once after paint.
    const id = requestAnimationFrame(() => {
      scrollToSelected();
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen, value]);

  // Re-sync the highlight when the value changes while the menu is open (e.g. cleared externally), so a stale option doesn't keep its highlight/checkmark.
  useEffect(() => {
    if (prevValueRef.current === value) return;
    prevValueRef.current = value;
    if (!isOpen) return;
    if (value === undefined) {
      setHighlightFlatIdx(-1);
      return;
    }
    const at = selectableIndices.findIndex((idx) => options[idx].value === value);
    setHighlightFlatIdx(at >= 0 ? at : -1);
  }, [value, isOpen, selectableIndices, options]);

  // Keep highlighted row in view while navigating with arrows.
  useEffect(() => {
    if (!isOpen || highlightedOptionIndex === undefined) return;
    if (!shouldScrollHighlightedRef.current) return;
    const menuEl = menuRef.current;
    if (!menuEl) return;
    const btn = menuEl.querySelector<HTMLElement>(`button[data-dropdown-option-index="${highlightedOptionIndex}"]`);
    if (!btn) return;
    const row = btn;
    const rect = row.getBoundingClientRect();
    const crect = menuEl.getBoundingClientRect();
    if (rect.top < crect.top) {
      menuEl.scrollTop += rect.top - crect.top - 4;
    } else if (rect.bottom > crect.bottom) {
      menuEl.scrollTop += rect.bottom - crect.bottom + 4;
    }
    shouldScrollHighlightedRef.current = false;
  }, [isOpen, highlightedOptionIndex]);

  const navigateHighlight = useCallback(
    (delta: number) => {
      if (selectableIndices.length === 0) return;
      setHighlightFlatIdx((prev) => {
        if (prev < 0) return delta > 0 ? 0 : selectableIndices.length - 1;
        return Math.min(Math.max(0, prev + delta), selectableIndices.length - 1);
      });
      shouldScrollHighlightedRef.current = true;
    },
    [selectableIndices.length],
  );

  const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    // Let Tab / Shift+Tab move focus; close menu first so list options don’t intercept order.
    if (e.key === "Tab") {
      if (isOpen) setIsOpen(false);
      return;
    }

    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (e.key === "ArrowDown") pendingKeyboardHighlightRef.current = "first";
        if (e.key === "ArrowUp") pendingKeyboardHighlightRef.current = "last";
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        navigateHighlight(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        navigateHighlight(-1);
        break;
      case "Enter":
      case " ": {
        e.preventDefault();
        const idx = highlightedOptionIndex;
        if (idx !== undefined && options[idx] && !options[idx].disabled) {
          handleSelect(options[idx].value);
        }
        break;
      }
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const hasGroups = options.some((opt) => opt.groupValue);

  const optionsList = (
    <>
      {options.map((option, index) => {
        const prevGroup = index > 0 ? options[index - 1]?.groupValue : undefined;
        const showGroupHeader = hasGroups && option.groupValue && option.groupValue !== prevGroup;
        return (
          // Render headers and buttons as siblings of the scroll container
          // (not nested inside per-option wrappers) so `position: sticky`
          // sticks to the scrollable list rather than to a tiny per-row div.
          <Fragment key={`${option.value}-${index}`}>
            {showGroupHeader && (
              <div
                className="flex items-center px-3 sticky top-0 z-10 select-none uppercase"
                style={{
                  background: "var(--color-card-muted, var(--color-card))",
                  color: "var(--foreground-muted, var(--color-foreground))",
                  fontSize: "var(--font-size-base, 12px)",
                  fontWeight: "var(--font-weights-medium, 500)",
                  height: "33px",
                  minHeight: "33px",
                }}
              >
                {option.groupValue}
              </div>
            )}
            <DropdownOptionRow
              option={option}
              index={index}
              isHighlighted={isOpen && !option.disabled && index === highlightedOptionIndex}
              isSelected={option.value === value}
              onSelect={handleSelect}
            />
          </Fragment>
        );
      })}
    </>
  );

  return (
    <ClickAwayListener onClickAway={() => isOpen && setIsOpen(false)}>
      <div
        ref={dropdownRef}
        className={cn("relative", fullWidth && "w-full", className)}
        style={maxWidth ? { maxWidth } : undefined}
      >
        <button
          type="button"
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleTriggerKeyDown}
          className={cn(
            "w-full min-h-[27px] rounded-lg border text-left cursor-pointer flex items-center py-1",
            "bg-[var(--color-surface-1)]",
            "text-[var(--foreground-default,var(--color-foreground))]",
            "border-[var(--outline-default)]",
            "hover:bg-[var(--color-surface-2)] hover:border-[var(--color-outline-variant)]",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-default)] focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:bg-[var(--colour-surface-disabled,#F0F0F0)] disabled:border-[var(--colour-surface-disabled,#F0F0F0)] disabled:text-[var(--colour-text-on-disabled,#A3A3A3)]",
            error && "border-[var(--color-danger)]",
          )}
          style={{
            fontSize: "var(--font-size-base, 12px)",
            height,
            // Override the default `min-h-[27px]` so the `height` prop fully controls the size.
            minHeight: height,
          }}
        >
          <span
            className={cn(
              "overflow-hidden text-ellipsis whitespace-nowrap block pl-3",
              !selectedOption && placeholder && "text-[var(--foreground-muted)]",
            )}
            style={{
              paddingRight: icon === "arrows" ? "1rem" : "2.25rem",
            }}
          >
            {displayValue}
          </span>
        </button>

        {clearable && selectedOption ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Clear selection"
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) onClear?.();
            }}
            className="absolute top-1/2 -translate-y-1/2 right-2 flex items-center justify-center cursor-pointer"
            style={{
              color: disabled ? "var(--color-neutral-on-disabled)" : "var(--color-neutral)",
            }}
          >
            <FontAwesomeIcon icon={faXmark} fontSize={14} />
          </button>
        ) : (
          <button
            type="button"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              !disabled && setIsOpen(!isOpen);
            }}
            className={cn(
              "absolute top-1/2 -translate-y-1/2 flex items-center justify-center cursor-pointer",
              icon === "arrows" ? "right-2" : "right-2",
              icon === "caret" && "transition-transform",
            )}
            style={{
              color: disabled
                ? "var(--color-neutral-on-disabled)"
                : icon === "arrows"
                  ? "var(--neutral-default)"
                  : "var(--color-neutral)",
              transform:
                icon === "caret" && isOpen ? "scale(0.7) rotate(180deg)" : icon === "caret" ? "scale(0.7)" : undefined,
            }}
          >
            {icon === "arrows" ? <ValueSelectArrows /> : <CaretDownIcon />}
          </button>
        )}

        {!portal && isOpen && !disabled && (
          <div
            ref={menuRef}
            data-click-away-inside
            role="listbox"
            className={cn(
              "absolute z-50 w-full rounded-lg border overflow-y-auto overflow-x-hidden shadow-lg scrollbar-thin",
              "bg-[var(--color-card)] border-[var(--outline-default)]",
              openUpward ? "bottom-full mb-1" : "top-full mt-1",
            )}
            style={{
              maxHeight: maxHeight,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {optionsList}
          </div>
        )}

        {portal &&
          isOpen &&
          !disabled &&
          portalPosition &&
          createPortal(
            <div
              ref={menuRef}
              data-click-away-inside
              role="listbox"
              className={cn(
                "fixed rounded-lg border overflow-y-auto overflow-x-hidden shadow-lg scrollbar-thin",
                "bg-[var(--color-card)] border-[var(--outline-default)]",
              )}
              style={{
                top: portalPosition.top,
                bottom: portalPosition.bottom,
                left: portalPosition.left,
                width: portalPosition.width,
                maxHeight: portalPosition.dropdownH,
                zIndex: 99999,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {optionsList}
            </div>,
            document.body,
          )}
      </div>
    </ClickAwayListener>
  );
});
